import { cn } from "@/lib/utils";

/**
 * Return initials for a person name.
 * - 0 parts (null/empty): "??"
 * - 1 part: first 2 letters, uppercased
 * - 2+ parts: first letter of first + first letter of last, uppercased
 */
export function getInitials(name: string | null | undefined): string {
  if (!name) return "??";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "??";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const sizeClasses = {
  sm: "h-6 w-6 text-xs",
  md: "h-8 w-8 text-sm",
  lg: "h-12 w-12 text-base",
} as const;

interface AvatarProps {
  name: string | null | undefined;
  avatarUrl?: string | null;
  size?: keyof typeof sizeClasses;
  className?: string;
}

/**
 * Shared Avatar component used across the app.
 * Renders an img if `avatarUrl` is set, otherwise a span with initials.
 */
export function Avatar({ name, avatarUrl, size = "md", className }: AvatarProps) {
  const initials = getInitials(name);
  const baseClass = cn(
    "inline-flex items-center justify-center rounded-full bg-muted text-muted-foreground font-medium",
    sizeClasses[size],
    className,
  );

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name ?? "User"}
        title={name ?? undefined}
        className={cn("inline-block rounded-full object-cover", sizeClasses[size], className)}
        onError={(e) => {
          // Fall back gracefully on load error — hide broken image.
          (e.currentTarget as HTMLImageElement).style.display = "none";
        }}
      />
    );
  }

  return (
    <span className={baseClass} title={name ?? undefined} aria-label={name ?? "User"}>
      {initials}
    </span>
  );
}
